import { ArrowUpRight, MapPin } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { PublicEvent, PublicTenant } from "@vzlive/shared";
import { t } from "@vzlive/shared/i18n";
import { BrandMark } from "../components/BrandMark";
import { StatusBadge } from "../components/StatusBadge";
import { apiFetch, ApiError } from "../lib/api";
import { tenantTheme } from "../lib/branding";

interface TenantResponse {
  tenant: PublicTenant;
  events: PublicEvent[];
}

export function EventIndexPage({ fallback }: { fallback: ReactNode }) {
  const [data, setData] = useState<TenantResponse | null>(null);
  const [missing, setMissing] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [search, setSearch] = useState("");
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    apiFetch<TenantResponse>("/api/public/tenant")
      .then((value) => {
        if (!cancelled) setData(value);
      })
      .catch((error) => {
        if (cancelled) return;
        if (error instanceof ApiError && error.status === 404) setMissing(true);
        else setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);
  useEffect(() => {
    if (!data) return;
    document.documentElement.lang = data.tenant.locale;
    document.title = `${data.tenant.name} · Live & Archiv`;
  }, [data?.tenant.name, data?.tenant.locale]);

  if (missing) return fallback;
  if (failed)
    return (
      <main className="reader-error" id="main">
        <h1>Die Events konnten nicht geladen werden.</h1>
        <p>Bitte prüfe deine Verbindung und versuche es erneut.</p>
        <button onClick={() => setAttempt((value) => value + 1)}>
          Erneut versuchen
        </button>
      </main>
    );
  if (!data)
    return (
      <main id="main" className="page-loader" role="status">
        <BrandMark />
        <p>Events werden geladen…</p>
      </main>
    );

  const { tenant, events } = data;
  const en = tenant.locale === "en";
  const upcoming = events
    .filter((event) => event.status !== "archived")
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  const featured =
    upcoming.find((event) => event.status === "live") ?? upcoming[0];
  const archive = events
    .filter((event) => event.status === "archived")
    .sort((a, b) => Date.parse(b.startsAt) - Date.parse(a.startsAt));
  const visibleArchive = archive.filter((event) =>
    `${event.title} ${event.venue ?? ""}`
      .toLocaleLowerCase(tenant.locale)
      .includes(search.trim().toLocaleLowerCase(tenant.locale)),
  );
  const formatDate = (date: string) =>
    new Intl.DateTimeFormat(tenant.locale, {
      day: "2-digit",
      month: "long",
      year: "numeric",
      timeZone: tenant.timezone,
    }).format(new Date(date));

  return (
    <div
      className="forum-tenant"
      style={tenantTheme(tenant.primaryColor, tenant.secondaryColor)}
    >
      <header className="reader-nav">
        <Link to="/" className="tenant-home" aria-label={tenant.name}>
          {tenant.logoUrl ? (
            <img src={tenant.logoUrl} alt={tenant.name} />
          ) : (
            <strong>{tenant.name}</strong>
          )}
        </Link>
        <nav aria-label={en ? "Main navigation" : "Hauptnavigation"}>
          <a href="#events">{en ? "Events" : "Programm"}</a>
          <a href="#archive">{t(tenant.locale, "archived")}</a>
          <Link to="/admin">
            {t(tenant.locale, "admin")}{" "}
            <ArrowUpRight size={16} aria-hidden="true" />
          </Link>
        </nav>
      </header>
      <main id="main">
        <section className="forum-tenant__intro">
          <h1>
            {en
              ? "Be part of the conversation."
              : "Dabei sein. Im Gespräch bleiben."}
          </h1>
          <p>
            {en
              ? `Live coverage from ${tenant.name}. Discover what is happening now, what comes next and the stories worth revisiting.`
              : `Die Liveblogs von ${tenant.name}: Entdecke, was gerade passiert, worauf du dich freuen kannst und welche Geschichten bleiben.`}
          </p>
        </section>
        {featured && (
          <section className="forum-featured">
            <div>
              <StatusBadge status={featured.status} locale={tenant.locale} />
              <h2>{featured.title}</h2>
              {featured.description && <p>{featured.description}</p>}
              <Link
                className="forum-button forum-button--primary"
                to={`/events/${featured.slug}`}
              >
                {en ? "Open liveblog" : "Liveblog öffnen"}{" "}
                <ArrowUpRight size={18} aria-hidden="true" />
              </Link>
            </div>
            <div className="forum-featured__context">
              <time dateTime={featured.startsAt}>
                {formatDate(featured.startsAt)}
                <span>
                  {new Intl.DateTimeFormat(tenant.locale, {
                    hour: "2-digit",
                    minute: "2-digit",
                    timeZone: tenant.timezone,
                  }).format(new Date(featured.startsAt))}
                  {en ? "" : " Uhr"}
                </span>
              </time>
              {featured.venue && (
                <p>
                  <MapPin size={18} aria-hidden="true" />
                  {featured.venue}
                </p>
              )}
            </div>
          </section>
        )}
        <section className="forum-event-list" id="events">
          <h2>{en ? "Coming up next" : "Das steht als Nächstes an."}</h2>
          {upcoming
            .filter((event) => event.id !== featured?.id)
            .map((event) => (
              <Link
                className="forum-event-row"
                to={`/events/${event.slug}`}
                key={event.id}
              >
                <time dateTime={event.startsAt}>
                  {formatDate(event.startsAt)}
                </time>
                <div>
                  <h3>{event.title}</h3>
                  {event.venue && <p>{event.venue}</p>}
                </div>
                <StatusBadge status={event.status} locale={tenant.locale} />
                <ArrowUpRight size={22} aria-hidden="true" />
              </Link>
            ))}
          {upcoming.filter((event) => event.id !== featured?.id).length ===
            0 && (
            <p className="forum-empty">
              {en
                ? "The next event will be announced here."
                : "Weitere Events kündigen wir hier an."}
            </p>
          )}
        </section>
        <section className="forum-event-list" id="archive">
          <header>
            <h2>{en ? "Worth another look." : "Zum Wiederlesen."}</h2>
            {archive.length > 0 && (
              <label className="forum-search">
                <span>{en ? "Search archive" : "Archiv durchsuchen"}</span>
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={en ? "Title or venue…" : "Titel oder Ort…"}
                />
              </label>
            )}
          </header>
          {visibleArchive.map((event) => (
            <Link
              className="forum-event-row"
              key={event.id}
              to={`/events/${event.slug}`}
            >
              <time dateTime={event.startsAt}>
                {formatDate(event.startsAt)}
              </time>
              <h3>{event.title}</h3>
              <ArrowUpRight size={22} aria-hidden="true" />
            </Link>
          ))}
          <p className="forum-empty" role="status">
            {!visibleArchive.length
              ? en
                ? search
                  ? "No matching events."
                  : "Past events will appear here."
                : search
                  ? "Keine passenden Events gefunden."
                  : "Hier findest du später die vergangenen Events."
              : search
                ? `${visibleArchive.length} ${en ? "events found" : "Events gefunden"}`
                : ""}
          </p>
        </section>
      </main>
      <footer className="reader-footer">
        <BrandMark />
        <span>{tenant.name}</span>
        <nav aria-label={en ? "Legal information" : "Rechtliche Informationen"}>
          {tenant.legal.imprintUrl && (
            <a href={tenant.legal.imprintUrl}>
              {en ? "Legal notice" : "Impressum"}
            </a>
          )}
          {tenant.legal.privacyUrl && (
            <a href={tenant.legal.privacyUrl}>
              {en ? "Privacy" : "Datenschutz"}
            </a>
          )}
        </nav>
      </footer>
    </div>
  );
}
